import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import AdLaunchService from 'services/AdLaunchService';
import SettingsService from 'services/SettingsService';
import EnhancementToggles from './EnhancementToggles';
import TemplateTable from './TemplateTable';
import { AdStatus, LaunchSettings } from './types';

const NAMESPACE = 'ad-launch';
const AD_TYPES = ['Static', 'Video', 'Short Video'];

function errorText(err: unknown): string {
  const anyErr = err as { response?: { data?: { error?: string } }; message?: string };
  return anyErr?.response?.data?.error || anyErr?.message || 'Something went wrong.';
}

export default function SettingsTab() {
  const service = useMemo(() => new AdLaunchService(), []);
  const settingsService = useMemo(() => new SettingsService(), []);
  const [settings, setSettings] = useState<LaunchSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState('');
  const [message, setMessage] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  const [newName, setNewName] = useState('');
  const [newCampaignId, setNewCampaignId] = useState('');
  const [newAdType, setNewAdType] = useState('Static');
  const [capturing, setCapturing] = useState(false);

  const load = useCallback(async () => {
    const res = await service.getSettings();
    if (res) setSettings(res);
    setLoading(false);
  }, [service]);

  useEffect(() => { load(); }, [load]);

  /**
   * Save a raw settings patch. Deliberately does NOT refetch: the previous
   * version awaited a full reload after every write, which reset the page.
   */
  const save = async (patch: Record<string, unknown>, optimistic?: Partial<LaunchSettings>) => {
    if (optimistic) setSettings(prev => (prev ? { ...prev, ...optimistic } : prev));
    try {
      await settingsService.updateOrThrow(NAMESPACE, patch);
    } catch (err) {
      setMessage({ kind: 'error', text: errorText(err) });
      await load(); // only on failure, to get back to the truth
    }
  };

  const capture = async () => {
    const reference = newCampaignId.trim();
    if (!newName.trim() || !reference) return;
    setCapturing(true);
    setMessage(null);
    try {
      const res = await service.captureTemplate(newName.trim(), reference, newAdType);
      await load();
      setNewName('');
      setNewCampaignId('');
      // Say which ad set and ad it resolved to, so pasting the wrong kind of id
      // is obvious immediately rather than at launch.
      const t = res.template;
      setMessage({
        kind: 'success',
        text: `Captured "${t.name}" from ${t.campaignName} — ad set "${t.sourceAdset?.name}", ad "${t.sourceAd?.name}".`,
      });
    } catch (err) {
      setMessage({ kind: 'error', text: errorText(err) });
    } finally {
      setCapturing(false);
    }
  };

  const patchTemplate = async (key: string, patch: Record<string, unknown>) => {
    setBusyKey(key);
    setMessage(null);
    try {
      await service.updateTemplate({ key, ...patch });
      await load();
    } catch (err) {
      setMessage({ kind: 'error', text: errorText(err) });
      await load();
    } finally {
      setBusyKey('');
    }
  };

  const resync = async (key: string) => {
    setBusyKey(key);
    setMessage(null);
    try {
      const res = await service.resyncTemplate(key);
      await load();
      setMessage({
        kind: 'success',
        text: res.changed
          ? `Re-synced "${res.template.name}" — ${res.changed} field${res.changed === 1 ? '' : 's'} changed: ${res.diff.map(d => d.path).join(', ')}.`
          : `"${res.template.name}" already matched its source ad set and ad — nothing changed.`,
      });
    } catch (err) {
      setMessage({ kind: 'error', text: errorText(err) });
    } finally {
      setBusyKey('');
    }
  };

  const recapture = async (key: string) => {
    const template = settings?.templates.find(t => t.key === key);
    if (!template) return;
    setBusyKey(key);
    setMessage(null);
    try {
      await service.captureTemplate(template.name, template.campaignId, template.adType);
      await load();
      setMessage({ kind: 'success', text: `Re-captured "${template.name}" from ${template.campaignName}.` });
    } catch (err) {
      setMessage({ kind: 'error', text: errorText(err) });
    } finally {
      setBusyKey('');
    }
  };

  const removeTemplate = async (key: string, name: string) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete the template "${name}"?`)) return;
    setBusyKey(key);
    try {
      await service.deleteTemplate(key);
      await load();
    } catch (err) {
      setMessage({ kind: 'error', text: errorText(err) });
    } finally {
      setBusyKey('');
    }
  };

  if (loading || !settings) {
    return <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: 2, maxWidth: 1100 }}>
      {message && <Alert severity={message.kind} sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}

      <Card variant="outlined" sx={{ mb: 2, maxWidth: 720 }}>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>Launch defaults</Typography>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              size="small"
              label="New ads are created"
              value={settings.defaultAdStatus}
              onChange={e => save({ defaultAdStatus: e.target.value }, { defaultAdStatus: e.target.value as AdStatus })}
              sx={{ maxWidth: 260 }}
              helperText="The Launch tab can override this per batch."
            >
              <MenuItem value="PAUSED">Paused — you turn them on</MenuItem>
              <MenuItem value="ACTIVE">Active — they can spend right away</MenuItem>
            </TextField>

            <Typography variant="body2" color="text.secondary">
              Ad account <code>act_{settings.accountId}</code> is fixed in code, and a batch can only be
              launched into a campaign named &ldquo;CBO - TESTING …&rdquo; — an allowlist that can be
              widened from a browser is not an allowlist.
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 2, maxWidth: 720 }}>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>Capture a template</Typography>
          <Typography variant="body2" color="text.secondary">
            Paste the id of an <strong>ad</strong>, an <strong>ad set</strong> or a <strong>campaign</strong>.
            How much gets pinned depends on which:
          </Typography>
          <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
            <Typography component="li" variant="body2" color="text.secondary">
              <strong>Ad</strong> — most precise. The ad set settings come from its ad set and the
              creative settings from that exact ad. Nothing is guessed.
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              <strong>Ad set</strong> — its settings are pinned; the creative half comes from the
              newest usable ad inside it.
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              <strong>Campaign</strong> — nothing pinned: the newest ad set and the newest ad. Convenient,
              but it drifts, and once this tool has launched a few batches the newest batch is one it made.
            </Typography>
          </Box>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              size="small"
              label="Template name"
              placeholder="Comforter — statics"
              value={newName}
              onChange={e => setNewName(e.target.value)}
            />
            <TextField
              size="small"
              label="Ad, ad set or campaign ID"
              placeholder="120252785948230514"
              value={newCampaignId}
              onChange={e => setNewCampaignId(e.target.value)}
              helperText="Any object in the account. A template built on a campaign that is not CBO - TESTING is kept and inspectable, but cannot be used to launch."
            />
            <TextField
              select
              size="small"
              label="Ad type"
              value={newAdType}
              onChange={e => setNewAdType(e.target.value)}
              sx={{ maxWidth: 220 }}
              helperText="Which batches this template is offered for."
            >
              {AD_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
            <Box>
              <Button
                variant="contained"
                size="small"
                onClick={capture}
                disabled={capturing || !newName.trim() || !newCampaignId.trim()}
              >
                {capturing ? <CircularProgress size={18} /> : 'Capture template'}
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Saved templates ({settings.templates.length})
      </Typography>
      <TemplateTable
        templates={settings.templates}
        accountId={settings.accountId}
        minSiteLinks={settings.minSiteLinks}
        busyKey={busyKey}
        onPatch={patchTemplate}
        onRecapture={recapture}
        onResync={resync}
        onDelete={removeTemplate}
      />

      <Card variant="outlined" sx={{ mt: 2 }}>
        <CardContent>
          <EnhancementToggles
            keys={settings.enhancementKeys}
            value={settings.creativeFeatures}
            observedLiveOptIn={settings.observedLiveOptIn}
            onSave={async features => {
              await settingsService.updateOrThrow(NAMESPACE, { enhancementsJson: JSON.stringify(features) });
              setSettings(prev => (prev ? { ...prev, creativeFeatures: features } : prev));
            }}
          />
        </CardContent>
      </Card>
    </Box>
  );
}
